'use client';
import React from 'react';
import { toast } from 'sonner';
import { Globe, TrendingUp, FileCheck, ShieldCheck, Users, Leaf, Calendar } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import StatusBadge from '@/components/ui/StatusBadge';

type Opportunity = {
  id: string;
  title: string;
  company: string;
  country: string;
  flag: string;
  sector: string;
  stage: string;
  fundingRequired: string;
  fundingUSD: number;
  shariahStatus: string;
  ddScore: number;
  expectedUse: string;
  description: string;
  financials: { revenue: string; growth: string; ebitda: string; employees: number };
  esgImpact: string;
  corridors: string[];
  documents: string[];
  interestCount: number;
};

interface InvestmentDetailModalProps {
  opportunity: Opportunity;
  onClose: () => void;
}

export default function InvestmentDetailModal({ opportunity, onClose }: InvestmentDetailModalProps) {
  const ddColor = (score: number) => {
    if (score >= 88) return 'var(--emerald-bright)';
    if (score >= 80) return 'var(--gold)';
    return 'var(--warning)';
  };

  return (
    <Modal open onClose={onClose} title="Investment Opportunity Details" size="xl">
      <div className="space-y-6">
        {/* Hero */}
        <div
          className="p-5 rounded-xl"
          style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{opportunity.flag}</span>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: 'rgba(201,168,76,0.12)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.2)' }}>
                  {opportunity.stage}
                </span>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: 'rgba(59,130,246,0.1)', color: 'var(--info)', border: '1px solid rgba(59,130,246,0.2)' }}>
                  {opportunity.sector}
                </span>
              </div>
              <h2 className="text-xl font-bold mb-1">{opportunity.title}</h2>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{opportunity.company} · {opportunity.country}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-extrabold font-mono-data" style={{ color: 'var(--gold)' }}>{opportunity.fundingRequired}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>Funding Target</p>
            </div>
          </div>
          <p className="text-sm leading-relaxed mt-4" style={{ color: 'var(--secondary-foreground)' }}>{opportunity.description}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Financial Snapshot */}
          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <TrendingUp size={15} style={{ color: 'var(--gold)' }} />
              Financial Snapshot
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Revenue', value: opportunity.financials.revenue },
                { label: 'Growth', value: opportunity.financials.growth },
                { label: 'EBITDA', value: opportunity.financials.ebitda },
                { label: 'Employees', value: opportunity.financials.employees.toString() },
              ].map((item) => (
                <div
                  key={`fin-${item.label}`}
                  className="rounded-xl p-3"
                  style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
                >
                  <p className="text-xs mb-1" style={{ color: 'var(--muted-foreground)' }}>{item.label}</p>
                  <p className="text-base font-bold font-mono-data">{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Compliance Status */}
          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <ShieldCheck size={15} style={{ color: 'var(--emerald-bright)' }} />
              Halal Compliance Status
            </h4>
            <div className="space-y-2">
              {[
                { label: 'Shariah Review', status: opportunity.shariahStatus === 'Completed' ? 'completed' : 'progress', value: opportunity.shariahStatus },
                { label: 'Due Diligence', status: 'valid', value: `${opportunity.ddScore}% Score` },
                { label: 'Investor Pack', status: 'valid', value: 'Available' },
                { label: 'Market Corridor', status: 'valid', value: 'Verified' },
              ].map((item) => (
                <div
                  key={`comp-${item.label}`}
                  className="flex items-center justify-between px-3 py-2.5 rounded-lg"
                  style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
                >
                  <span className="text-sm">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono-data" style={{ color: 'var(--muted-foreground)' }}>{item.value}</span>
                    <StatusBadge variant={item.status as 'valid' | 'completed' | 'progress'} label={item.status === 'completed' ? 'Completed' : item.status === 'progress' ? 'In Progress' : 'Verified'} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ESG Impact */}
        <div>
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Leaf size={15} style={{ color: 'var(--emerald-bright)' }} />
            ESG & Ethical Impact
          </h4>
          <div
            className="p-4 rounded-xl"
            style={{ background: 'rgba(10, 107, 79, 0.06)', border: '1px solid rgba(10, 107, 79, 0.2)' }}
          >
            <p className="text-sm leading-relaxed" style={{ color: 'var(--secondary-foreground)' }}>{opportunity.esgImpact}</p>
          </div>
        </div>

        {/* Market Corridors */}
        <div>
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Globe size={15} style={{ color: 'var(--info)' }} />
            Market Corridor Potential
          </h4>
          <div className="flex flex-wrap gap-2">
            {opportunity.corridors.map((corridor) => (
              <span
                key={`detcorridor-${opportunity.id}-${corridor}`}
                className="px-3 py-1.5 rounded-full text-sm font-medium"
                style={{ background: 'rgba(59,130,246,0.1)', color: 'var(--info)', border: '1px solid rgba(59,130,246,0.2)' }}
              >
                {corridor}
              </span>
            ))}
          </div>
        </div>

        {/* Due Diligence Documents */}
        <div>
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <FileCheck size={15} style={{ color: 'var(--gold)' }} />
            Due Diligence Documents
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {opportunity.documents.map((doc) => (
              <div
                key={`doc-${opportunity.id}-${doc}`}
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors hover:bg-muted"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
                onClick={() => toast.success(`${doc} — access request submitted`)}
              >
                <FileCheck size={14} style={{ color: 'var(--emerald-bright)' }} />
                <span className="text-xs font-medium">{doc}</span>
                <span className="ml-auto text-xs" style={{ color: 'var(--muted-foreground)' }}>Request</span>
              </div>
            ))}
          </div>
        </div>

        {/* Investor Interest */}
        <div
          className="flex items-center gap-3 p-4 rounded-xl"
          style={{ background: 'rgba(59, 130, 246, 0.06)', border: '1px solid rgba(59, 130, 246, 0.2)' }}
        >
          <Users size={18} style={{ color: 'var(--info)' }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--info)' }}>{opportunity.interestCount} investors have expressed interest</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Join the investor queue to receive updates and investor pack access</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={() => { toast.success('Investment pack requested'); onClose(); }}
            className="flex-1 btn-gold flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-semibold"
          >
            <TrendingUp size={15} />
            Request Investor Pack
          </button>
          <button
            onClick={() => { toast.success('Meeting request submitted — team will contact you within 24 hours'); onClose(); }}
            className="flex-1 btn-primary flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-semibold"
          >
            <Calendar size={15} />
            Schedule Meeting
          </button>
        </div>
      </div>
    </Modal>
  );
}